/* eslint-disable no-unused-vars */
/* eslint-disable react-hooks/exhaustive-deps */
import { useEffect, useRef, useState } from 'react';

/* PACKAGES */
import { Helmet } from 'react-helmet';
import { Button, Dialog, DialogPanel, DialogTitle } from '@headlessui/react';

/* COMPONENTS */
import Topbar from '../../../PulseSurvey/Topbar';
import Loader from '../../../../component/common/Loader';

/* ICONS */
import { RiLink } from 'react-icons/ri';
import { IoIosArrowForward } from 'react-icons/io';
import { Api } from '../../../../api';
import { useParams } from 'react-router-dom';
import moment from 'moment';
import { errorToast, successToast, uploadImage } from '../../../../utils/helper';
import { useFormik } from 'formik';
import * as Yup from 'yup';
import { HiOutlineTrash } from 'react-icons/hi2';
import { GoPlus } from 'react-icons/go';

const validationSchema = Yup.object({
  paid_amount: Yup.number().required('Paid Amount is required'),
  invoice_number: Yup.string().required('Invoice Number is required'),
  notes: Yup.string(),
  file: Yup.mixed().required('Document is required')
});

const PeapInvoice = () => {
  // PARAMS
  const params = useParams();

  const invoiceId = params.id;

  /* INPUT REF */
  const fileInputRef = useRef(null);

  // STATE VARIABLES
  const [loading, setLoading] = useState(false);
  const [invoiceDetails, setInvoiceDetails] = useState(null);
  const [isLoadingUpdate, setIsLoadingUpdate] = useState(false);
  const [selectedFile, setSelectedFile] = useState(null);
  const [confirmationDialog, setConfirmationDialog] = useState(false);

  // FUNCTIONS
  const handleGetInvoiceDetailsFunction = async () => {
    setLoading(true);
    try {
      const response = await Api.getTransactionDetails(invoiceId);
      if (response?.data) {
        setInvoiceDetails(response?.data?.data);
        if (response?.data?.data?.invoice_number) {
          formik.setFieldValue('invoice_number', response?.data?.data?.invoice_number);
        }
        if (response?.data?.data?.paid_amount) {
          formik.setFieldValue('paid_amount', response?.data?.data?.paid_amount);
        }

        if (response?.data?.data?.notes) {
          formik.setFieldValue('notes', response?.data?.data?.notes);
        }
      }
    } catch (error) {
      console.log('Error while getting Invoice Details', error);
    } finally {
      setLoading(false);
    }
  };

  const initialValues = {
    paid_amount: '',
    invoice_number: '',
    notes: '',
    file: null
  };

  const formik = useFormik({
    initialValues,
    validationSchema,
    onSubmit: async (values) => {
      await handlePaymentApprove(values);
    }
  });

  const handlePaymentApprove = (payoutData) => {
    const reqBody = {
      file: payoutData?.file?.fileType,
      paidAmount: payoutData?.paid_amount,
      invoice_number: payoutData?.invoice_number,
      notes: payoutData?.notes,
      status: 1,
      id: invoiceId
    };
    setIsLoadingUpdate(true);
    Api.updatePayoutStatus(reqBody)
      .then(async (response) => {
        if (response?.data?.meta?.code === 1) {
          const meta = response?.data?.meta;
          if (meta?.filename && meta?.uploadURL) {
            const uploadImageResponse = await uploadImage(
              payoutData?.file?.file,
              meta?.uploadURL,
              meta?.filename
            );
            if (uploadImageResponse?.status === 200) {
              setIsLoadingUpdate(false);
              successToast('Image uploaded successfully');
            }
          } else {
            setIsLoadingUpdate(false);
          }
          successToast(response?.data?.meta?.message);
          handleGetInvoiceDetailsFunction();
          setConfirmationDialog(!confirmationDialog);
        } else {
          errorToast(response?.data?.meta?.message);
        }
      })
      .catch((error) => {
        setIsLoadingUpdate(false);
        errorToast(error);
      })
      .finally(() => {
        setIsLoadingUpdate(true);
      });
  };

  /* FORMAT FILE SIZE */
  const formatFileSize = (bytes) => {
    if (bytes === 0) return '0 Bytes';
    const k = 1024;
    const sizes = ['Bytes', 'KB', 'MB', 'GB'];
    const i = Math.floor(Math.log(bytes) / Math.log(k));
    return parseFloat((bytes / Math.pow(k, i)).toFixed(2)) + ' ' + sizes[i];
  };

  /* FILE SELECT */
  const handleFileSelect = (e) => {
    const file = e.target.files[0];
    if (file) {
      const fileSize = formatFileSize(file.size);
      formik.setFieldValue('file', {
        file: file,
        fileType: file.type,
        fileName: file.name,
        extension: file.name.split('.').pop(),
        fileSize: fileSize,
        link: URL.createObjectURL(file)
      });
      setSelectedFile(file);
    }
  };

  /* HANDLE FILE CLICK */
  const handleFileClick = () => {
    fileInputRef.current?.click();
  };

  /* HANDLE FILE DELETE */
  const handleFileDelete = () => {
    if (formik.values['file']?.link) {
      URL.revokeObjectURL(formik.values['file'].link);
    }
    formik.setFieldValue('file', null);
    setSelectedFile(null);
  };

  /* FILEDATA */
  const fileData = formik.values['file'];

  // USE-EFFECTS
  useEffect(() => {
    if (invoiceId) {
      handleGetInvoiceDetailsFunction();
    }
  }, [invoiceId]);

  return (
    <form className="relative pl-5">
      {loading && <Loader />}

      {/* HELMET */}
      <Helmet>
        <meta charSet="utf-8" />
        <title>Invoice Details | Shoorah Admin</title>
      </Helmet>

      {/* TOPBAR */}
      <Topbar
        title="Invoice details"
        hasDesc
        description="You are currently reviewing the details of a PEAP expert invoice."
      />

      {/* DETAILS */}
      <div className="mt-10">
        <div className="bg-white dark:bg-shoorah-darkBgTabColor border border-[#E7E8EA] dark:border-transparent rounded-lg">
          <div className="p-5">
            <div className="grid grid-cols-2 gap-14">
              {/* LEFT SECTION */}
              <div className="col-span-1">
                {/* EXPERT DETAILS */}
                <div className="mt-0">
                  {/* HEADING */}
                  <div className="p-0">
                    <h5 className="text-base font-medium text-black dark:text-white">
                      Expert details
                    </h5>
                  </div>

                  <div className="mt-5">
                    {/* NAME */}
                    <div className="mt-0">
                      <label className="text-sm font-normal text-black dark:text-white">Name</label>
                      <div className="mt-1">
                        <input
                          type="text"
                          disabled
                          name="name"
                          value={invoiceDetails?.expert_user_id?.name}
                          className="w-full block disabled:bg-[#F4F4F4] dark:disabled:bg-white/10 rounded-lg px-4 py-3 text-black dark:text-white bg-white border border-[#E7E8EA] dark:disabled:border-transparent text-sm"
                        />
                      </div>
                    </div>

                    {/* EMAIL */}
                    <div className="mt-5">
                      <label className="text-sm font-normal text-black dark:text-white">
                        Email
                      </label>
                      <div className="mt-1">
                        <input
                          type="email"
                          disabled
                          name="email"
                          value={invoiceDetails?.expert_user_id?.email}
                          className="w-full block disabled:bg-[#F4F4F4] dark:disabled:bg-white/10 rounded-lg px-4 py-3 text-black dark:text-white bg-white border border-[#E7E8EA] dark:disabled:border-transparent text-sm"
                        />
                      </div>
                    </div>

                    {/* ADDRESS */}
                    <div className="mt-5">
                      <label className="text-sm font-normal text-black dark:text-white">
                        Address
                      </label>
                      <div className="mt-1">
                        <input
                          type="text"
                          disabled
                          name="address"
                          value={invoiceDetails?.expert_user_id?.country}
                          className="w-full block disabled:bg-[#F4F4F4] dark:disabled:bg-white/10 rounded-lg px-4 py-3 text-black dark:text-white bg-white border border-[#E7E8EA] dark:disabled:border-transparent text-sm"
                        />
                      </div>
                    </div>
                  </div>
                </div>

                {/* INVOICE DETAILS */}
                <div className="mt-10">
                  {/* HEADING */}
                  <div className="p-0">
                    <h5 className="text-base font-medium text-black dark:text-white">
                      Invoice details
                    </h5>
                  </div>

                  <div className="mt-5">
                    {/* NAME */}
                    <div className="mt-0">
                      <label className="text-sm font-normal text-black dark:text-white">
                        Invoice number
                      </label>
                      <div className="mt-1">
                        <input
                          type="text"
                          disabled={invoiceDetails?.status === 1}
                          name="invoice_number"
                          onChange={formik.handleChange}
                          value={formik.values.invoice_number}
                          className="w-full block disabled:bg-[#F4F4F4] dark:disabled:bg-white/10 rounded-lg px-4 py-3 text-black dark:text-white bg-white border border-[#E7E8EA] dark:disabled:border-transparent text-sm"
                        />
                      </div>
                    </div>

                    {/* SUBMISSION DATE */}
                    <div className="mt-5">
                      <label className="text-sm font-normal text-black dark:text-white">
                        Date of submission
                      </label>
                      <div className="mt-1">
                        <input
                          type="text"
                          disabled
                          name="submissionDate"
                          value={moment(invoiceDetails?.invoice_date).format('DD/MM/YYYY hh:mm A')}
                          className="w-full block disabled:bg-[#F4F4F4] dark:disabled:bg-white/10 rounded-lg px-4 py-3 text-black dark:text-white bg-white border border-[#E7E8EA] dark:disabled:border-transparent text-sm"
                        />
                      </div>
                    </div>

                    {/* AMOUNT */}
                    <div className="mt-5">
                      <label className="text-sm font-normal text-black dark:text-white">
                        Amount (£)
                      </label>
                      <div className="mt-1">
                        <input
                          type="text"
                          disabled
                          name="amount"
                          value={invoiceDetails?.amount}
                          className="w-full block disabled:bg-[#F4F4F4] dark:disabled:bg-white/10 rounded-lg px-4 py-3 text-black dark:text-white bg-white border border-[#E7E8EA] dark:disabled:border-transparent text-sm"
                        />
                      </div>
                    </div>

                    {/* PAID AMOUNT */}
                    <div className="mt-5">
                      <label className="text-sm font-normal text-black dark:text-white">
                        Paid Amount (£)
                      </label>
                      <div className="mt-1">
                        <input
                          type="text"
                          disabled={invoiceDetails?.status === 1}
                          name="paid_amount"
                          onChange={formik.handleChange}
                          value={formik.values.paid_amount}
                          placeholder="Enter the amount paid by Shoorah"
                          className="w-full block disabled:bg-[#F4F4F4] dark:disabled:bg-white/10 rounded-lg px-4 py-3 text-black dark:text-white bg-white border border-[#E7E8EA] dark:disabled:border-transparent text-sm"
                        />
                      </div>
                    </div>
                  </div>
                </div>
              </div>

              {/* RIGHT SECTION */}
              <div className="col-span-1">
                <div className="mt-0">
                  {/* HEADING */}
                  <div className="p-0">
                    <h5 className="text-base font-medium text-black dark:text-white">
                      Attached Invoice
                    </h5>
                  </div>

                  {/* FILE BOX */}
                  <div className="mt-5 bg-[#F4F4F4] dark:bg-shoorah-darkBgColor rounded-lg p-5">
                    <p className="text-sm font-normal text-black dark:text-white">
                      The expert has attached an invoice.
                    </p>

                    {/* FILE */}
                    <div className="mt-3">
                      <img
                        src={invoiceDetails?.expert_attachments}
                        className="w-full h-auto object-cover rounded-lg"
                        alt="Invoice"
                      />
                    </div>
                  </div>
                </div>

                {/* ATTACH INVOICE/PREVIEW */}
                <div className="mt-5">
                  {/* HEADING */}
                  <div className="p-0">
                    <h5 className="text-base font-medium text-black dark:text-white">
                      Attach Invoice
                    </h5>
                  </div>

                  {/* FILE BOX */}
                  {!invoiceDetails?.attachments && invoiceDetails?.status !== 1 && (
                    <div className="mt-3 bg-[#F4F4F4] dark:bg-shoorah-darkBgColor rounded-lg p-5">
                      <p className="text-sm font-normal text-black dark:text-white">
                        You have attached a file, which will also be visible on the company's side.
                      </p>

                      {/* FILE */}
                      <div className="mt-5">
                        {/* FILE INPUT */}
                        <input
                          type="file"
                          ref={fileInputRef}
                          onChange={handleFileSelect}
                          className="hidden"
                          accept="*/*"
                        />

                        {/* FILE UPLOAD OPTION */}
                        {(fileData === null || fileData === '' || fileData === undefined) && (
                          <div
                            className="group flex items-center gap-3 cursor-pointer"
                            onClick={handleFileClick}
                          >
                            <div className="w-8 h-8 bg-white dark:bg-shoorah-darkBgTabColor flex items-center justify-center rounded-md">
                              <GoPlus className="w-5 h-5" />
                            </div>
                            <p className="text-sm font-medium text-black dark:text-white group-hover:text-shoorah-primary">
                              Add new file
                            </p>
                          </div>
                        )}

                        {/* UPLOADED FILE */}
                        {fileData && (
                          <div className="bg-white dark:bg-shoorah-darkBgTabColor border border-[#E7E8EA] dark:border-transparent rounded-lg py-3 px-4">
                            <div className="flex items-center justify-between">
                              <div className="p-0">
                                <div className="flex items-center gap-4">
                                  <RiLink className="w-5 h-5 text-black dark:text-white flex-shrink-0" />
                                  <div className="p-0">
                                    <h5 className="text-sm font-medium text-black dark:text-white">
                                      {fileData.fileName}
                                    </h5>
                                    <p className="mt-1 text-xs font-normal text-black/80 dark:text-white/80">
                                      File size: {fileData.fileSize}
                                    </p>
                                  </div>
                                </div>
                              </div>
                              <Button
                                onClick={handleFileDelete}
                                className="bg-[#E35949] hover:bg-opacity-80 transition-all duration-300 ease-in-out rounded-md p-2"
                              >
                                <HiOutlineTrash className="w-5 h-5 text-white" />
                              </Button>
                            </div>
                          </div>
                        )}
                      </div>

                      {/* OPTIONS */}
                      {fileData && (
                        <div className="mt-5 flex items-center gap-2">
                          <a
                            href={fileData.link}
                            download={fileData.fileName}
                            rel="noreferrer"
                            target="_blank"
                            className="text-sm font-normal text-black dark:text-white hover:text-shoorah-primary transition-all duration-300 ease-in-out"
                          >
                            Download file
                          </a>
                          <p className="text-sm font-normal text-black dark:text-white">|</p>
                          <p
                            // onClick={handleFileClick}
                            className="text-sm font-normal text-black dark:text-white hover:text-shoorah-primary transition-all duration-300 ease-in-out cursor-pointer"
                          >
                            Replace file
                          </p>
                        </div>
                      )}

                      {/* VALIDATION */}
                      {formik.touched.file && formik.errors.file && (
                        <p className="mt-4 text-xs font-normal text-red-400">
                          Please attach the invoice
                        </p>
                      )}
                    </div>
                  )}

                  {invoiceDetails?.attachments && invoiceDetails?.status === 1 && (
                    <div className="mt-5 bg-[#F4F4F4] dark:bg-shoorah-darkBgColor rounded-lg p-5">
                      <p className="text-sm font-normal text-black dark:text-white">
                        You have attached a file, which will also be visible on the company's side.
                      </p>

                      {/* FILE */}
                      <div className="mt-5">
                        <div className="bg-white dark:bg-shoorah-darkBgTabColor border border-[#E7E8EA] dark:border-transparent rounded-lg py-3 px-4">
                          <div className="flex items-center gap-4">
                            <RiLink className="w-5 h-5 text-black dark:text-white flex-shrink-0" />
                            <div className="p-0">
                              <h5 className="text-sm font-medium text-black dark:text-white">
                                {invoiceDetails?.attachments?.split('/')[5] || '-'}
                                {console.log('invoiceDetails?.attachments', invoiceDetails)}
                              </h5>
                            </div>
                          </div>
                        </div>
                      </div>

                      {/* OPTIONS */}
                      <div className="mt-5 flex items-center gap-2">
                        <a
                          href={invoiceDetails?.attachments}
                          rel="noreferrer"
                          target="_blank"
                          className="text-sm font-normal text-black dark:text-white hover:text-shoorah-primary transition-all duration-300 ease-in-out cursor-pointer"
                        >
                          Download file
                        </a>
                      </div>
                    </div>
                  )}
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* SECOND SECTION */}
      <div className="mt-5">
        <div className="grid grid-cols-2 gap-5">
          {/* INTERNAL NOTES */}
          <div className="col-span-1">
            <div className="bg-white dark:bg-shoorah-darkBgTabColor border border-[#E7E8EA] dark:border-transparent rounded-lg h-full">
              <div className="p-5">
                {/* HEADING */}
                <div className="p-0">
                  <h5 className="text-base font-medium text-black dark:text-white">
                    Internal notes
                  </h5>
                </div>

                {/* CONTENT */}
                <div className="mt-5">
                  <div className="bg-[#F4F4F4] dark:bg-shoorah-darkBgTabColor rounded-t-lg border border-[#E7E8EA] dark:border-shoorah-darkBorder px-4 py-3">
                    <p className="text-sm font-medium text-black dark:text-white">Add notes</p>
                  </div>
                  <div className="border border-[#E7E8EA] dark:border-shoorah-darkBorder border-t-0 rounded-b-lg">
                    <textarea
                      rows={4}
                      name="notes"
                      disabled={invoiceDetails?.status === 1}
                      onChange={formik.handleChange}
                      placeholder="Internal notes"
                      value={formik.values.notes}
                      className="w-full block disabled:bg-white dark:disabled:bg-white/10 px-4 py-2 rounded-lg text-black dark:text-white bg-white dark:bg-transparent text-sm"
                    />
                  </div>
                </div>
              </div>
            </div>
          </div>

          {/* INTERNAL STATUS */}
          <div className="col-span-1">
            <div className="h-full bg-white dark:bg-shoorah-darkBgTabColor border border-[#E7E8EA] dark:border-transparent rounded-lg">
              <div className="p-5">
                {/* HEADING */}
                <div className="p-0">
                  <h5 className="text-base font-medium text-black dark:text-white">
                    Internal status
                  </h5>
                  <p className="text-sm font-normal text-black/80 dark:text-white/80">
                    Update the status of this invoice to ensure we can confirm payment from the
                    company.
                  </p>
                </div>

                {/* CONTENT */}
                <div className="mt-5">
                  {invoiceDetails?.status === 2 && (
                    <div className="w-full bg-yellow-400 bg-opacity-10 px-4 py-3 border border-yellow-500 rounded-lg">
                      <div className="flex items-center justify-between">
                        <p className="text-sm font-medium text-yellow-600">Pending</p>
                      </div>
                    </div>
                  )}

                  {invoiceDetails?.status === 1 && (
                    <div className="w-full bg-green-400 bg-opacity-10 px-4 py-3 border border-green-500 rounded-lg">
                      <div className="flex items-center justify-between">
                        <p className="text-sm font-medium text-green-600">Completed</p>
                      </div>
                    </div>
                  )}

                  {invoiceDetails?.status !== 1 && (
                    <div className="mt-5">
                      <Button
                        type="button"
                        disabled={isLoadingUpdate}
                        onClick={() => setConfirmationDialog(!confirmationDialog)}
                        className="btn-primary"
                      >
                        {isLoadingUpdate ? 'Please wait...' : 'Update status'}
                      </Button>
                    </div>
                  )}
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* CONFIRMATION DIALOG */}
      <Dialog
        open={confirmationDialog}
        onClose={() => setConfirmationDialog(!confirmationDialog)}
        className="relative z-50"
      >
        <div className="fixed inset-0 flex w-screen items-center justify-center p-4 bg-black bg-opacity-50">
          <DialogPanel className="max-w-lg space-y-4 border bg-white rounded-xl shadow-lg p-8">
            <div className="pb-5 border-b border-[#E7E8EA]">
              <DialogTitle className="font-bold text-lg">Update status</DialogTitle>
              <p>Are you sure you want to update the status of this invoice?</p>
            </div>
            <div className="flex items-center justify-end gap-5">
              <button type="button" onClick={() => setConfirmationDialog(!confirmationDialog)}>
                Cancel
              </button>
              <button
                type="button"
                onClick={() => formik.handleSubmit()}
                className="btn-primary"
              >
                Mark as Paid
              </button>
            </div>
          </DialogPanel>
        </div>
      </Dialog>
    </form>
  );
};

export default PeapInvoice;
